import React from 'react';
import {
  Compass,
  ArrowLeft,
  Layers,
  LayoutDashboard,
  ShieldCheck,
  AlertTriangle,
  ClipboardCheck,
  ListTodo,
  FileText
} from 'lucide-react';
import { MODULE_META_CATALOG, setPageMeta } from '../shared/meta';

interface NotFoundPageProps {
  invalidTab?: string;
  onNavigate: (tabId: string) => void;
}

export const NotFoundPage: React.FC<NotFoundPageProps> = ({
  invalidTab,
  onNavigate,
}) => {
  React.useEffect(() => {
    setPageMeta(MODULE_META_CATALOG['not_found']);
  }, []);

  const quickLinks = [
    { id: 'modules', label: 'Platform Modules', icon: <Layers size={18} color="#2563eb" />, desc: 'All platform management modules' },
    { id: 'risks', label: 'Risk & Bowtie', icon: <ShieldCheck size={18} color="#059669" />, desc: '5x5 risk matrix & visual barriers' },
    { id: 'occurrences', label: 'Occurrences', icon: <AlertTriangle size={18} color="#d97706" />, desc: 'Mandatory & voluntary safety reports' },
    { id: 'audits', label: 'Quality Audits', icon: <ClipboardCheck size={18} color="#7c3aed" />, desc: 'Annual audit programme & findings' },
    { id: 'capas', label: 'CAPA Management', icon: <ListTodo size={18} color="#dc2626" />, desc: 'Corrective & preventive actions' },
    { id: 'ingestion', label: 'Safety Reports', icon: <FileText size={18} color="#0284c7" />, desc: 'Central report intake wizard' },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '75vh',
        padding: '40px 24px',
        textAlign: 'center',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* 404 Visual Hero Card */}
      <div
        style={{
          maxWidth: '720px',
          width: '100%',
          background: '#ffffff',
          borderRadius: '24px',
          border: '1px solid #e2e8f0',
          boxShadow: '0 20px 40px -15px rgba(15, 23, 42, 0.08)',
          padding: '48px 36px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Top Decorative Banner Accent */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: 'linear-gradient(90deg, #2563eb 0%, #38bdf8 50%, #f59e0b 100%)',
          }}
        />

        {/* Radar / Compass Icon Badge */}
        <div
          style={{
            width: '96px',
            height: '96px',
            borderRadius: '50%',
            background: '#eff6ff',
            border: '2px dashed #93c5fd',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px',
            color: '#2563eb',
            boxShadow: '0 8px 16px rgba(37, 99, 235, 0.12)',
          }}
        >
          <Compass size={48} strokeWidth={1.75} />
        </div>

        {/* 404 Badge */}
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '12px',
            fontWeight: 800,
            letterSpacing: '0.08em',
            padding: '4px 14px',
            borderRadius: '999px',
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca',
            marginBottom: '16px',
          }}
        >
          <span>●</span> 404 — PAGE NOT FOUND
        </div>

        {/* Title */}
        <h1
          style={{
            fontSize: '32px',
            fontWeight: 800,
            color: '#0f172a',
            margin: '0 0 12px 0',
            letterSpacing: '-0.02em',
          }}
        >
          Page or Module Not Found
        </h1>

        {/* Description */}
        <p
          style={{
            fontSize: '15px',
            color: '#64748b',
            lineHeight: 1.6,
            maxWidth: '560px',
            margin: '0 0 24px 0',
          }}
        >
          The requested safety, quality, or compliance module is not registered or its route may have changed.
          {invalidTab && (
            <span
              style={{
                display: 'block',
                marginTop: '10px',
                fontSize: '13px',
                color: '#334155',
                background: '#f1f5f9',
                padding: '6px 12px',
                borderRadius: '8px',
                fontFamily: 'monospace',
              }}
            >
              Requested parameter: <strong>?tab={invalidTab}</strong>
            </span>
          )}
        </p>

        {/* Action Buttons */}
        <div
          style={{
            display: 'flex',
            gap: '12px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            marginBottom: '36px',
          }}
        >
          <button
            onClick={() => onNavigate('modules')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              borderRadius: '12px',
              background: '#2563eb',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
              transition: 'all 0.15s ease',
            }}
          >
            <Layers size={18} />
            Back to Platform Modules
          </button>

          <button
            onClick={() => onNavigate('dashboard')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 20px',
              borderRadius: '12px',
              background: '#f8fafc',
              color: '#334155',
              fontSize: '14px',
              fontWeight: 600,
              border: '1px solid #cbd5e1',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <LayoutDashboard size={18} />
            Go to Dashboard
          </button>

          <button
            onClick={() => window.history.back()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 18px',
              borderRadius: '12px',
              background: '#ffffff',
              color: '#64748b',
              fontSize: '14px',
              fontWeight: 600,
              border: '1px solid #e2e8f0',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <ArrowLeft size={16} />
            Go Back
          </button>
        </div>

        {/* Quick Access Popular Modules */}
        <div style={{ width: '100%', borderTop: '1px solid #f1f5f9', paddingTop: '28px' }}>
          <div
            style={{
              fontSize: '12px',
              fontWeight: 700,
              color: '#94a3b8',
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              marginBottom: '16px',
            }}
          >
            Quick Access Modules
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
              gap: '12px',
              textAlign: 'left',
            }}
          >
            {quickLinks.map((link) => (
              <div
                key={link.id}
                onClick={() => onNavigate(link.id)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  padding: '12px 14px',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  background: '#f8fafc',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#ffffff';
                  e.currentTarget.style.borderColor = '#93c5fd';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 12px rgba(0,0,0,0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f8fafc';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.transform = 'none';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <div style={{ marginTop: '2px', flexShrink: 0 }}>{link.icon}</div>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{link.label}</div>
                  <div style={{ fontSize: '11px', color: '#64748b', marginTop: '2px', lineHeight: 1.3 }}>{link.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
